import { Entity, EntityType } from "@/queries/folder-entities/types";

export const sortEntities = (entities: Entity[]): Entity[] =>
  [...entities].sort((a, b) => {
    // Folders first, then by date
    if (a.type === EntityType.FOLDER && b.type !== EntityType.FOLDER) {
      return -1;
    }
    if (a.type !== EntityType.FOLDER && b.type === EntityType.FOLDER) {
      return 1;
    }

    // For same type, sort by updated_at or fallback to created_at (most recent first)
    const aDateString = a.updated_at || a.created_at;
    const bDateString = b.updated_at || b.created_at;
    const aDate = aDateString ? new Date(aDateString).getTime() : 0;
    const bDate = bDateString ? new Date(bDateString).getTime() : 0;
    return bDate - aDate;
  });

export const getChildEntities = (entities: Entity[], parentId: number | null): Entity[] => {
  const children = entities.filter(e => {
    // Root level items have null or undefined folder_id
    if (parentId === null) {
      return !e.folder_id;
    }
    return e.folder_id === parentId;
  });
  return sortEntities(children);
};

export const buildExpandedFoldersSet = (
  entities: Entity[],
  currentEntityInfo: { type: EntityType; id: number | string } | null,
): Set<number> => {
  if (!currentEntityInfo) {
    return new Set<number>();
  }

  const folderMap = new Map<number, Entity>();
  const entityMap = new Map<string, Entity>();

  entities.forEach(entity => {
    if (entity.type === EntityType.FOLDER && typeof entity.id === "number") {
      folderMap.set(entity.id, entity);
    }
    entityMap.set(`${entity.type}-${String(entity.id)}`, entity);
  });

  let targetFolderId: number | null = null;

  // If we're viewing a folder directly
  if (currentEntityInfo.type === EntityType.FOLDER) {
    if (typeof currentEntityInfo.id === "number") {
      targetFolderId = currentEntityInfo.id;
    } else {
      const parsedFolderId = Number(currentEntityInfo.id);
      targetFolderId = Number.isNaN(parsedFolderId) ? null : parsedFolderId;
    }
  } else {
    // Otherwise, check if we're viewing an entity (including string-ID entities like smart tables) that's in a folder
    const currentEntity = entityMap.get(
      `${currentEntityInfo.type}-${String(currentEntityInfo.id)}`,
    );
    const entityFolderId = currentEntity?.folder_id;
    if (entityFolderId !== undefined && entityFolderId !== null) {
      targetFolderId = entityFolderId;
    }
  }

  if (targetFolderId === null) {
    return new Set<number>();
  }

  // Build path from target folder to root
  const pathToExpand: number[] = [];
  let current = folderMap.get(targetFolderId);

  while (current) {
    if (typeof current.id === "number") {
      pathToExpand.unshift(current.id);
    }
    if (current.folder_id !== undefined && current.folder_id !== null) {
      current = folderMap.get(current.folder_id);
    } else {
      break;
    }
  }

  return new Set(pathToExpand);
};
